import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { MessageCircle, Send, Loader2, User, Bot, Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function ChatTakeoverPage() {
  const { authHeaders, API, activeBranch } = useAuth();
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => { if (activeBranch) fetchSessions(); }, [activeBranch]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchSessions = async () => {
    if (!activeBranch) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/takeover/sessions?branch_id=${activeBranch.id}`, authHeaders());
      setSessions(res.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  const selectSession = async (sessionId) => {
    setSelectedSession(sessionId);
    try {
      const res = await axios.get(`${API}/takeover/messages/${sessionId}`, authHeaders());
      setMessages(res.data);
    } catch { toast.error(t('takeover.loadFailed')); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selectedSession) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/takeover/reply`, {
        session_id: selectedSession, message: reply.trim()
      }, authHeaders());
      if (res.data.success) {
        setMessages(prev => [...prev, res.data.message]);
        setReply('');
        toast.success(t('takeover.sent'));
      }
    } catch { toast.error(t('takeover.sendFailed')); }
    finally { setSending(false); }
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('takeover.timeNow');
    if (mins < 60) return `${mins} ${t('takeover.timeMinutes')}`;
    if (mins < 1440) return `${Math.floor(mins / 60)} ${t('takeover.timeHours')}`;
    return `${Math.floor(mins / 1440)} ${t('takeover.timeDays')}`;
  };

  return (
    <ModuleLayout moduleKey="ai">
      <div className="space-y-4" data-testid="chat-takeover-page">
        <div>
          <h1 className="font-bold text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            <MessageCircle className="w-6 h-6 text-primary" /> {t('takeover.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('takeover.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ height: 'calc(100vh - 200px)' }}>
          {/* Sessions List */}
          <Card className="lg:col-span-1 overflow-hidden flex flex-col">
            <div className="p-3 border-b border-border">
              <p className="text-sm font-semibold text-foreground">{t('takeover.activeSessions', { count: sessions.length })}</p>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {loading ? (
                <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">{t('takeover.noSessions')}</div>
              ) : (
                sessions.map(s => (
                  <div key={s.session_id}
                    onClick={() => selectSession(s.session_id)}
                    className={`p-3 border-b border-border/50 cursor-pointer transition-colors hover:bg-muted/50 ${selectedSession === s.session_id ? 'bg-primary/5 border-r-2 border-r-primary' : ''}`}
                    data-testid={`session-${s.session_id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <span className="text-xs font-medium text-foreground truncate max-w-[140px]">{s.session_id.slice(0, 12)}...</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> {getTimeSince(s.last_at)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mr-9">
                      {s.last_role === 'user' ? '👤 ' : '🤖 '}{s.last_message?.slice(0, 50)}
                    </p>
                    <Badge variant="outline" className="text-[10px] mt-1">{s.message_count} {t('takeover.messageCount')}</Badge>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Chat View */}
          <Card className="lg:col-span-2 overflow-hidden flex flex-col">
            {!selectedSession ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <ChevronRight className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">{t('takeover.selectSession')}</p>
                </div>
              </div>
            ) : (
              <>
                <div className="p-3 border-b border-border bg-muted/30">
                  <p className="text-sm font-semibold text-foreground">{t('takeover.conversationLabel')} {selectedSession.slice(0, 20)}...</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                  {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'} gap-2`}>
                      {msg.role === 'user' && (
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                          <User className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                      )}
                      <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : msg.is_human
                            ? 'bg-emerald-500/10 text-foreground border border-emerald-500/30'
                            : 'bg-muted text-foreground'
                      }`}>
                        {msg.is_human && <span className="text-[10px] text-emerald-600 block mb-1 font-semibold">👤 {msg.owner_name || t('takeover.ownerLabel')}</span>}
                        {msg.content}
                      </div>
                      {msg.role === 'assistant' && (
                        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: msg.is_human ? '#10B98120' : '#D9770620' }}>
                          {msg.is_human ? <User className="w-3.5 h-3.5 text-emerald-600" /> : <Bot className="w-3.5 h-3.5 text-primary" />}
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-3 border-t border-border">
                  <div className="flex gap-2">
                    <Input value={reply} onChange={e => setReply(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') sendReply(); }}
                      placeholder={t('takeover.replyPlaceholder')}
                      className="flex-1 h-10 text-sm" disabled={sending}
                      data-testid="takeover-reply-input"
                    />
                    <Button onClick={sendReply} disabled={!reply.trim() || sending}
                      className="h-10 px-4 rounded-full" data-testid="takeover-send-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">{t('takeover.replyNote')}</p>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </ModuleLayout>
  );
}
